import { z } from 'zod';
import { activityItemSchema, activityQuerySchema } from './activity';
import {
  adminInviteListSchema,
  adminInviteSchema,
  adminUserListSchema,
  adminUserSchema,
  createInviteRequestSchema,
  inviteSchema,
  myInviteListSchema,
} from './admin';
import {
  accountCreatedResponseSchema,
  deviceSessionListSchema,
  invitePreviewSchema,
  passkeyListSchema,
  passkeyLoginVerifyRequestSchema,
  passkeyOptionsResponseSchema,
  passkeyRegisterVerifyRequestSchema,
  passkeySchema,
  recoveryRotateResponseSchema,
  recoverySignInRequestSchema,
  redeemInviteRequestSchema,
  sessionUserResponseSchema,
  setupRequestSchema,
} from './auth';
import {
  feedItemSchema,
  groupBalancesSchema,
  overviewSchema,
  peopleListSchema,
  personDetailSchema,
} from './balances';
import { okResponseSchema, pageSchema, paginationQuerySchema } from './common';
import {
  createExpenseRequestSchema,
  expenseDetailSchema,
  expenseResponseSchema,
  receiptSchema,
  updateExpenseRequestSchema,
} from './expenses';
import { fxRatesQuerySchema, rateTableSchema } from './fx';
import {
  addMemberRequestSchema,
  createGroupRequestSchema,
  directGroupRequestSchema,
  groupDetailSchema,
  groupListSchema,
  joinGroupRequestSchema,
  joinGroupResponseSchema,
  joinPreviewSchema,
  joinTokenResponseSchema,
  linkPlaceholderRequestSchema,
  memberResponseSchema,
  muteGroupRequestSchema,
  renamePlaceholderRequestSchema,
  updateGroupRequestSchema,
} from './groups';
import {
  notificationListQuerySchema,
  notificationListSchema,
  notificationPreferencesSchema,
  pushStatusSchema,
  pushSubscriptionRequestSchema,
  pushUnsubscribeRequestSchema,
  unreadCountSchema,
  updateNotificationPreferencesRequestSchema,
  vapidPublicKeySchema,
} from './notifications';
import { createSettlementRequestSchema, settlementResponseSchema } from './settlements';
import { meSchema, updateMeRequestSchema, userProfileSchema } from './users';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
/** `public` = no session; `user` = any active account; `admin` = app admin role. */
export type RouteAuth = 'public' | 'user' | 'admin';

export interface RouteDef {
  readonly method: HttpMethod;
  /** Hono-style path with `:params`. */
  readonly path: string;
  readonly auth: RouteAuth;
  readonly summary: string;
  readonly body?: z.ZodType;
  readonly query?: z.ZodType;
  /** Success response body schema (status `status`, default 200). */
  readonly response: z.ZodType;
  readonly status?: number;
}

const def = <const T extends RouteDef>(d: T): T => d;

/**
 * Every API endpoint. The web app can use this to build typed fetchers:
 * `z.infer<typeof routes.createExpense.response>` etc., and `buildPath(routes.x.path, params)`.
 */
export const routes = {
  // ---- System ---------------------------------------------------------------------------
  health: def({
    method: 'GET',
    path: '/api/health',
    auth: 'public',
    summary: 'Liveness check',
    response: z.object({ ok: z.literal(true), setupDone: z.boolean() }),
  }),
  setup: def({
    method: 'POST',
    path: '/api/setup',
    auth: 'public',
    summary: 'Bootstrap the first admin with ADMIN_SETUP_SECRET (works once)',
    body: setupRequestSchema,
    response: accountCreatedResponseSchema,
    status: 201,
  }),

  // ---- Auth -----------------------------------------------------------------------------
  invitePreview: def({
    method: 'GET',
    path: '/api/invites/:code',
    auth: 'public',
    summary: 'Check an invite code (status, inviter, suggested name)',
    response: invitePreviewSchema,
  }),
  redeemInvite: def({
    method: 'POST',
    path: '/api/invites/:code/redeem',
    auth: 'public',
    summary: 'Create an account from an invite; returns the recovery phrase once and signs in',
    body: redeemInviteRequestSchema,
    response: accountCreatedResponseSchema,
    status: 201,
  }),
  myInvites: def({
    method: 'GET',
    path: '/api/invites',
    auth: 'user',
    summary: 'Invites created by the caller, with status',
    response: myInviteListSchema,
  }),
  createInvite: def({
    method: 'POST',
    path: '/api/invites',
    auth: 'user',
    summary: 'Create a single-use account invite (20 per rolling 24 h unless admin; then 429)',
    body: createInviteRequestSchema,
    response: inviteSchema,
    status: 201,
  }),
  revokeMyInvite: def({
    method: 'POST',
    path: '/api/invites/:id/revoke',
    auth: 'user',
    summary: "Revoke one of the caller's unused invites (404 if not theirs, 409 if not unused)",
    response: inviteSchema,
  }),
  recoverySignIn: def({
    method: 'POST',
    path: '/api/auth/recovery',
    auth: 'public',
    summary: 'Sign in on a new device with the 12-word recovery phrase',
    body: recoverySignInRequestSchema,
    response: sessionUserResponseSchema,
  }),
  passkeyLoginOptions: def({
    method: 'POST',
    path: '/api/auth/passkeys/options',
    auth: 'public',
    summary: 'Start a passkey sign-in (discoverable credentials)',
    response: passkeyOptionsResponseSchema,
  }),
  passkeyLoginVerify: def({
    method: 'POST',
    path: '/api/auth/passkeys/verify',
    auth: 'public',
    summary: 'Finish a passkey sign-in and create a session',
    body: passkeyLoginVerifyRequestSchema,
    response: sessionUserResponseSchema,
  }),
  session: def({
    method: 'GET',
    path: '/api/auth/session',
    auth: 'user',
    summary: 'Current user (401 when signed out)',
    response: sessionUserResponseSchema,
  }),
  logout: def({
    method: 'POST',
    path: '/api/auth/logout',
    auth: 'user',
    summary: 'Sign out of this device (revokes the session and its push subscription)',
    response: okResponseSchema,
  }),

  // ---- Me -------------------------------------------------------------------------------
  me: def({
    method: 'GET',
    path: '/api/me',
    auth: 'user',
    summary: 'Own profile',
    response: meSchema,
  }),
  updateMe: def({
    method: 'PATCH',
    path: '/api/me',
    auth: 'user',
    summary: 'Update profile, payment handles, locale, theme, default currency, timezone',
    body: updateMeRequestSchema,
    response: meSchema,
  }),
  rotateRecovery: def({
    method: 'POST',
    path: '/api/me/recovery/rotate',
    auth: 'user',
    summary: 'Issue a new recovery phrase (old one stops working)',
    response: recoveryRotateResponseSchema,
  }),
  listSessions: def({
    method: 'GET',
    path: '/api/me/sessions',
    auth: 'user',
    summary: 'Signed-in devices',
    response: deviceSessionListSchema,
  }),
  revokeSession: def({
    method: 'DELETE',
    path: '/api/me/sessions/:id',
    auth: 'user',
    summary: 'Sign a device out',
    response: okResponseSchema,
  }),
  listPasskeys: def({
    method: 'GET',
    path: '/api/me/passkeys',
    auth: 'user',
    summary: 'Registered passkeys',
    response: passkeyListSchema,
  }),
  passkeyRegisterOptions: def({
    method: 'POST',
    path: '/api/me/passkeys/options',
    auth: 'user',
    summary: 'Start adding a passkey',
    response: passkeyOptionsResponseSchema,
  }),
  passkeyRegisterVerify: def({
    method: 'POST',
    path: '/api/me/passkeys',
    auth: 'user',
    summary: 'Finish adding a passkey',
    body: passkeyRegisterVerifyRequestSchema,
    response: z.object({ passkey: passkeySchema }),
    status: 201,
  }),
  deletePasskey: def({
    method: 'DELETE',
    path: '/api/me/passkeys/:id',
    auth: 'user',
    summary: 'Remove a passkey',
    response: okResponseSchema,
  }),

  // ---- Users & people ----------------------------------------------------------------------
  userProfile: def({
    method: 'GET',
    path: '/api/users/:id',
    auth: 'user',
    summary: 'Public profile (payment handles only if you share a group)',
    response: userProfileSchema,
  }),
  people: def({
    method: 'GET',
    path: '/api/people',
    auth: 'user',
    summary: 'Everyone you share a group with and your 1:1 balance with them',
    response: peopleListSchema,
  }),
  person: def({
    method: 'GET',
    path: '/api/people/:userId',
    auth: 'user',
    summary: '1:1 balance with one person, per shared group',
    response: personDetailSchema,
  }),
  directGroup: def({
    method: 'POST',
    path: '/api/direct',
    auth: 'user',
    summary: 'Get or create the hidden 1:1 group with someone you share a group with',
    body: directGroupRequestSchema,
    response: groupDetailSchema,
  }),
  overview: def({
    method: 'GET',
    path: '/api/balances',
    auth: 'user',
    summary: 'Home: overall net, per-group and per-person balances',
    response: overviewSchema,
  }),

  // ---- Groups ---------------------------------------------------------------------------
  listGroups: def({
    method: 'GET',
    path: '/api/groups',
    auth: 'user',
    summary: 'My groups with my balance in each (includes archived and direct)',
    response: groupListSchema,
  }),
  createGroup: def({
    method: 'POST',
    path: '/api/groups',
    auth: 'user',
    summary: 'Create a group (caller becomes group admin)',
    body: createGroupRequestSchema,
    response: groupDetailSchema,
    status: 201,
  }),
  getGroup: def({
    method: 'GET',
    path: '/api/groups/:id',
    auth: 'user',
    summary: 'Group with members and join link',
    response: groupDetailSchema,
  }),
  updateGroup: def({
    method: 'PATCH',
    path: '/api/groups/:id',
    auth: 'user',
    summary: 'Rename, emoji, currency, simplify, archive (group admin)',
    body: updateGroupRequestSchema,
    response: groupDetailSchema,
  }),
  deleteGroup: def({
    method: 'DELETE',
    path: '/api/groups/:id',
    auth: 'user',
    summary: 'Delete a group for everyone (group admin)',
    response: okResponseSchema,
  }),
  leaveGroup: def({
    method: 'POST',
    path: '/api/groups/:id/leave',
    auth: 'user',
    summary: 'Leave a group (blocked with a non-zero balance)',
    response: okResponseSchema,
  }),
  resetJoinLink: def({
    method: 'POST',
    path: '/api/groups/:id/join-link/reset',
    auth: 'user',
    summary: 'Invalidate the group link and create a new one (group admin)',
    response: joinTokenResponseSchema,
  }),
  muteGroup: def({
    method: 'PUT',
    path: '/api/groups/:id/mute',
    auth: 'user',
    summary: 'Mute or unmute a group for me',
    body: muteGroupRequestSchema,
    response: okResponseSchema,
  }),
  joinPreview: def({
    method: 'GET',
    path: '/api/join/:token',
    auth: 'user',
    summary: 'Preview a group link',
    response: joinPreviewSchema,
  }),
  joinGroup: def({
    method: 'POST',
    path: '/api/join/:token',
    auth: 'user',
    summary: 'Join via group link, optionally claiming a placeholder',
    body: joinGroupRequestSchema,
    response: joinGroupResponseSchema,
  }),
  addMember: def({
    method: 'POST',
    path: '/api/groups/:id/members',
    auth: 'user',
    summary: 'Add a placeholder ({ name }) or an account you share a group with ({ userId })',
    body: addMemberRequestSchema,
    response: memberResponseSchema,
    status: 201,
  }),
  renamePlaceholder: def({
    method: 'PATCH',
    path: '/api/groups/:id/members/:memberId',
    auth: 'user',
    summary: 'Rename a placeholder',
    body: renamePlaceholderRequestSchema,
    response: memberResponseSchema,
  }),
  linkPlaceholder: def({
    method: 'POST',
    path: '/api/groups/:id/members/:memberId/link',
    auth: 'user',
    summary: 'Link a placeholder to an account (group admin; merges if already a member)',
    body: linkPlaceholderRequestSchema,
    response: memberResponseSchema,
  }),
  removeMember: def({
    method: 'DELETE',
    path: '/api/groups/:id/members/:memberId',
    auth: 'user',
    summary: 'Remove a member or placeholder (group admin; zero balance only)',
    response: okResponseSchema,
  }),
  groupFeed: def({
    method: 'GET',
    path: '/api/groups/:id/feed',
    auth: 'user',
    summary: 'Expenses and settlements, newest first (30 per page)',
    query: paginationQuerySchema.extend({ includeDeleted: z.enum(['true', 'false']).optional() }),
    response: pageSchema(feedItemSchema),
  }),
  groupBalances: def({
    method: 'GET',
    path: '/api/groups/:id/balances',
    auth: 'user',
    summary: 'Balances converted and per currency, plus simplified (or pairwise) debts',
    response: groupBalancesSchema,
  }),
  groupActivity: def({
    method: 'GET',
    path: '/api/groups/:id/activity',
    auth: 'user',
    summary: 'Activity log of one group',
    query: paginationQuerySchema,
    response: pageSchema(activityItemSchema),
  }),

  // ---- Expenses -------------------------------------------------------------------------
  createExpense: def({
    method: 'POST',
    path: '/api/groups/:id/expenses',
    auth: 'user',
    summary: 'Add an expense (fx snapshot stored when the currency differs)',
    body: createExpenseRequestSchema,
    response: expenseResponseSchema,
    status: 201,
  }),
  getExpense: def({
    method: 'GET',
    path: '/api/expenses/:id',
    auth: 'user',
    summary: 'Expense with edit history',
    response: expenseDetailSchema,
  }),
  updateExpense: def({
    method: 'PATCH',
    path: '/api/expenses/:id',
    auth: 'user',
    summary: 'Edit an expense (creator, payer or group admin)',
    body: updateExpenseRequestSchema,
    response: expenseResponseSchema,
  }),
  deleteExpense: def({
    method: 'DELETE',
    path: '/api/expenses/:id',
    auth: 'user',
    summary: 'Delete (soft) an expense',
    response: expenseResponseSchema,
  }),
  restoreExpense: def({
    method: 'POST',
    path: '/api/expenses/:id/restore',
    auth: 'user',
    summary: 'Restore a deleted expense (any group member)',
    response: expenseResponseSchema,
  }),
  uploadReceipt: def({
    method: 'PUT',
    path: '/api/expenses/:id/receipt',
    auth: 'user',
    summary: 'Upload the receipt photo (raw image body, max 5 MB)',
    response: z.object({ receipt: receiptSchema }),
  }),
  getReceipt: def({
    method: 'GET',
    path: '/api/expenses/:id/receipt',
    auth: 'user',
    summary: 'Download the receipt image (binary)',
    response: z.unknown(),
  }),
  deleteReceipt: def({
    method: 'DELETE',
    path: '/api/expenses/:id/receipt',
    auth: 'user',
    summary: 'Remove the receipt',
    response: okResponseSchema,
  }),

  // ---- Settlements ----------------------------------------------------------------------
  createSettlement: def({
    method: 'POST',
    path: '/api/groups/:id/settlements',
    auth: 'user',
    summary: 'Mark as paid / record a payment received (status pending)',
    body: createSettlementRequestSchema,
    response: settlementResponseSchema,
    status: 201,
  }),
  getSettlement: def({
    method: 'GET',
    path: '/api/settlements/:id',
    auth: 'user',
    summary: 'One settlement',
    response: settlementResponseSchema,
  }),
  confirmSettlement: def({
    method: 'POST',
    path: '/api/settlements/:id/confirm',
    auth: 'user',
    summary: 'Confirm a pending settlement (the other side)',
    response: settlementResponseSchema,
  }),
  rejectSettlement: def({
    method: 'POST',
    path: '/api/settlements/:id/reject',
    auth: 'user',
    summary: '"Not received": status disputed, balance restored',
    response: settlementResponseSchema,
  }),
  undoRejectSettlement: def({
    method: 'POST',
    path: '/api/settlements/:id/undo-reject',
    auth: 'user',
    summary: 'Undo "Not received" within the undo window (back to pending)',
    response: settlementResponseSchema,
  }),
  cancelSettlement: def({
    method: 'POST',
    path: '/api/settlements/:id/cancel',
    auth: 'user',
    summary: 'Withdraw your own pending settlement',
    response: settlementResponseSchema,
  }),

  // ---- Activity -------------------------------------------------------------------------
  activity: def({
    method: 'GET',
    path: '/api/activity',
    auth: 'user',
    summary: 'Activity across my groups (filter with ?groupId=)',
    query: activityQuerySchema,
    response: pageSchema(activityItemSchema),
  }),

  // ---- Notifications --------------------------------------------------------------------
  notifications: def({
    method: 'GET',
    path: '/api/notifications',
    auth: 'user',
    summary: 'Inbox, newest first, with unread count',
    query: notificationListQuerySchema,
    response: notificationListSchema,
  }),
  unreadCount: def({
    method: 'GET',
    path: '/api/notifications/unread-count',
    auth: 'user',
    summary: 'Badge count',
    response: unreadCountSchema,
  }),
  markNotificationRead: def({
    method: 'POST',
    path: '/api/notifications/:id/read',
    auth: 'user',
    summary: 'Mark one notification read',
    response: unreadCountSchema,
  }),
  markAllNotificationsRead: def({
    method: 'POST',
    path: '/api/notifications/read-all',
    auth: 'user',
    summary: 'Mark everything read',
    response: unreadCountSchema,
  }),
  notificationPreferences: def({
    method: 'GET',
    path: '/api/notifications/preferences',
    auth: 'user',
    summary: 'Push switches, quiet hours, muted groups',
    response: notificationPreferencesSchema,
  }),
  updateNotificationPreferences: def({
    method: 'PATCH',
    path: '/api/notifications/preferences',
    auth: 'user',
    summary: 'Change push switches / quiet hours / timezone',
    body: updateNotificationPreferencesRequestSchema,
    response: notificationPreferencesSchema,
  }),
  vapidPublicKey: def({
    method: 'GET',
    path: '/api/push/vapid-public-key',
    auth: 'public',
    summary: 'VAPID application server key (base64url)',
    response: vapidPublicKeySchema,
  }),
  pushStatus: def({
    method: 'GET',
    path: '/api/push/subscription',
    auth: 'user',
    summary: 'Whether this device has a push subscription',
    response: pushStatusSchema,
  }),
  pushSubscribe: def({
    method: 'POST',
    path: '/api/push/subscription',
    auth: 'user',
    summary: 'Register this device for push',
    body: pushSubscriptionRequestSchema,
    response: pushStatusSchema,
    status: 201,
  }),
  pushUnsubscribe: def({
    method: 'DELETE',
    path: '/api/push/subscription',
    auth: 'user',
    summary: 'Turn push off on this device',
    body: pushUnsubscribeRequestSchema,
    response: pushStatusSchema,
  }),
  pushTest: def({
    method: 'POST',
    path: '/api/push/test',
    auth: 'user',
    summary: 'Send a silent test push to this device',
    response: okResponseSchema,
    status: 202,
  }),

  // ---- FX -------------------------------------------------------------------------------
  fxRates: def({
    method: 'GET',
    path: '/api/fx/rates',
    auth: 'user',
    summary: 'ECB rates (EUR base) for a date, cached daily',
    query: fxRatesQuerySchema,
    response: rateTableSchema,
  }),

  // ---- Admin ----------------------------------------------------------------------------
  adminInvites: def({
    method: 'GET',
    path: '/api/admin/invites',
    auth: 'admin',
    summary: 'All invites with status and creator',
    response: adminInviteListSchema,
  }),
  adminCreateInvite: def({
    method: 'POST',
    path: '/api/admin/invites',
    auth: 'admin',
    summary: 'Create a single-use invite',
    body: createInviteRequestSchema,
    response: adminInviteSchema,
    status: 201,
  }),
  adminRevokeInvite: def({
    method: 'POST',
    path: '/api/admin/invites/:id/revoke',
    auth: 'admin',
    summary: 'Revoke any unused invite',
    response: adminInviteSchema,
  }),
  adminUsers: def({
    method: 'GET',
    path: '/api/admin/users',
    auth: 'admin',
    summary: 'All accounts (no balances)',
    response: adminUserListSchema,
  }),
  adminDisableUser: def({
    method: 'POST',
    path: '/api/admin/users/:id/disable',
    auth: 'admin',
    summary: 'Disable an account, revoke its sessions and its unused invites',
    response: adminUserSchema,
  }),
  adminEnableUser: def({
    method: 'POST',
    path: '/api/admin/users/:id/enable',
    auth: 'admin',
    summary: 'Re-enable an account',
    response: adminUserSchema,
  }),
} as const satisfies Record<string, RouteDef>;

export type Routes = typeof routes;
export type RouteName = keyof Routes;

/** Response body type of a route. */
export type RouteResponse<K extends RouteName> = z.infer<Routes[K]['response']>;
/** Request body type of a route (input side, before defaults/transforms). */
export type RouteBody<K extends RouteName> = Routes[K] extends { body: z.ZodType }
  ? z.input<Routes[K]['body']>
  : never;

/** Fills `:params` in a path: `buildPath('/api/groups/:id', { id: 'abc' })`. */
export function buildPath(path: string, params: Readonly<Record<string, string>> = {}): string {
  return path.replace(/:([A-Za-z]+)/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`Missing path parameter "${name}" for ${path}`);
    return encodeURIComponent(value);
  });
}
